/**
 * Formatting utilities for validation results table
 */

/**
 * Format date to "Day, Month DD, YYYY" format
 * Example: "Tuesday, October 28, 2025"
 */
export function formatDate(date: Date | string | undefined): string {
  if (!date) {
    date = new Date();
  }
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  return dateObj.toLocaleDateString('en-US', options);
}

/**
 * Format amount as "€ XX XXX" with space as thousands separator
 * Example: "€ 52 340"
 */
export function formatAmount(amount: number | undefined, currency: string = 'EUR'): string {
  if (amount === undefined || amount === null) {
    return '€ 0';
  }
  
  // Format with space as thousands separator
  const formatted = Math.abs(amount).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const symbol = currency === 'EUR' ? '€' : currency;
  
  return `${symbol} ${formatted}`;
}

/**
 * Map country codes to full country names
 */
const COUNTRY_MAP: Record<string, string> = {
  'DE': 'Germany',
  'IR': 'Iran',
  'FR': 'France',
  'GB': 'United Kingdom',
  'US': 'United States',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'IT': 'Italy',
  'ES': 'Spain',
  'AT': 'Austria',
  'CH': 'Switzerland',
  'PL': 'Poland',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'PT': 'Portugal',
  'GR': 'Greece',
  'IE': 'Ireland',
  'LU': 'Luxembourg',
  'CZ': 'Czech Republic',
  'SK': 'Slovakia',
  'HU': 'Hungary',
  'RO': 'Romania',
  'BG': 'Bulgaria',
  'HR': 'Croatia',
  'SI': 'Slovenia',
  'EE': 'Estonia',
  'LV': 'Latvia',
  'LT': 'Lithuania',
  'MT': 'Malta',
  'CY': 'Cyprus',
  // High-risk countries
  'KP': 'North Korea',
  'MM': 'Myanmar',
  'AF': 'Afghanistan',
  'SY': 'Syria',
  'YE': 'Yemen',
  'LY': 'Libya',
  'SO': 'Somalia',
  'IQ': 'Iraq',
  'SS': 'South Sudan',
  'ET': 'Ethiopia',
  'VN': 'Vietnam',
  'TR': 'Turkey',
  'PH': 'Philippines',
  'TZ': 'Tanzania',
  'UG': 'Uganda',
  'ZW': 'Zimbabwe',
};

/**
 * Extract country code from IBAN and return full country name
 */
export function getCountryName(iban: string | undefined): string {
  if (!iban || iban.length < 2) {
    return 'N/A';
  }
  
  const countryCode = iban.substring(0, 2).toUpperCase();
  return COUNTRY_MAP[countryCode] || countryCode;
}

/**
 * Extract and format rule name from validation messages or issues
 */
export function extractRuleName(
  validationMessages: string[] | undefined,
  issues: Array<{ code?: string; message?: string }> | undefined
): string {
  // Check issues first (they contain alert information)
  if (issues && issues.length > 0) {
    for (const issue of issues) {
      if (issue.code === 'ALERT' && issue.message) {
        const msg = issue.message.toLowerCase();
        
        // High-Risk Country
        if (msg.includes('high-risk') || msg.includes('high risk') || msg.includes('high-risk third country')) {
          return 'High-Risk Country';
        }
        
        // Linked Transactions > €50,000 (24h)
        if (msg.includes('linked') && (msg.includes('50') || msg.includes('50000'))) {
          // Check if it mentions hours
          if (msg.includes('24') || msg.includes('hours')) {
            return 'Linked Transactions > €50,000 (24h)';
          }
          return 'Linked Transactions > €50,000';
        }
        
        // More than 3 incoming payments
        if (msg.includes('more than 3 incoming') || msg.includes('3 incoming')) {
          return 'Linked Transactions';
        }
        
        // More than 4 outgoing above 15k
        if (msg.includes('more than 4 outgoing') || msg.includes('4 outgoing')) {
          return 'Multiple Outgoing Transactions';
        }
        
        // Non-FATF country
        if (msg.includes('non-fatf') || msg.includes('non fatf')) {
          return 'Non-FATF Country';
        }
      }
    }
  }
  
  // Check validation messages
  if (validationMessages && validationMessages.length > 0) {
    for (const msg of validationMessages) {
      const lowerMsg = msg.toLowerCase();
      
      if (lowerMsg.includes('high-risk country') || lowerMsg.includes('high risk')) {
        return 'High-Risk Country';
      }
      if (lowerMsg.includes('linked') && (lowerMsg.includes('50') || lowerMsg.includes('50000'))) {
        if (lowerMsg.includes('24') || lowerMsg.includes('hours')) {
          return 'Linked Transactions > €50,000 (24h)';
        }
        return 'Linked Transactions > €50,000';
      }
      if (lowerMsg.includes('linked') && lowerMsg.includes('transaction')) {
        return 'Linked Transactions';
      }
      if (lowerMsg.includes('amount') && lowerMsg.includes('threshold')) {
        return 'Amount Threshold Exceeded';
      }
      if (lowerMsg.includes('suspicious')) {
        return 'Suspicious Transaction';
      }
    }
  }
  
  return 'N/A';
}

